import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import "./Layout.css";

const NAV_ITEMS = [
  { path: "/",                      label: "Dashboard",       icon: "⌂" },
  { path: "/buy-on-close",          label: "Buy on Close",    icon: "🕒" },
  { path: "/straight-purchase",     label: "Straight Purchase",icon: "🛒" },
  { path: "/price-based-running-man", label: "Price Based RM", icon: "📈" },
  { path: "/trailing-sell",         label: "Trailing Sell",   icon: "📉" },
  { path: "/analytics",            label: "Analytics",       icon: "📊" },
  { path: "/simulation",           label: "Simulation",      icon: "🧪" },
];

function getCSTTime() {
  return new Date().toLocaleTimeString("en-US", {
    timeZone: "America/Chicago",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

function getStoredTheme() {
  try {
    return localStorage.getItem("theme") || "dark";
  } catch {
    return "dark";
  }
}

export default function Layout({ children, onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme);
  const [clock, setClock] = useState(getCSTTime);

  useEffect(() => {
    const id = setInterval(() => setClock(getCSTTime()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try { localStorage.setItem("theme", theme); } catch (_) { /* storage unavailable */ }
  }, [theme]);

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  const currentPage = NAV_ITEMS.find(
    (n) => n.path === location.pathname
  )?.label || "Dashboard";

  return (
    <div className="layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="layout__overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? "sidebar--open" : ""}`}>
        <div className="sidebar__brand">
          <span className="sidebar__logo">H</span>
          <span className="sidebar__brandText">StreetAdvise</span>
        </div>

        <nav className="sidebar__nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                `sidebar__link ${isActive ? "sidebar__link--active" : ""}`
              }
            >
              <span className="sidebar__icon">{item.icon}</span>
              <span className="sidebar__label">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main area */}
      <div className="layout__main">
        {/* Top bar */}
        <header className="topbar">
          <button
            className="topbar__menu"
            onClick={() => setSidebarOpen((s) => !s)}
            aria-label="Toggle navigation"
          >
            <span className="topbar__hamburger" />
          </button>

          <h1 className="topbar__title">{currentPage}</h1>

          <div className="topbar__right">
            <div className="topbar__clock" title="Central Standard Time">
              <span className="topbar__clockLabel">CST</span>
              <span className="topbar__clockTime">{clock}</span>
            </div>

            <button
              className="topbar__themeBtn"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              {theme === "dark" ? "☀" : "☾"}
            </button>

            <button
              className="topbar__logoutBtn"
              onClick={onLogout}
              aria-label="Sign out"
              title="Sign out"
            >
              Sign out
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="layout__content">
          {children}
        </main>
      </div>
    </div>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  onLogout: PropTypes.func.isRequired,
};
